import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Alert } from '@mui/material';
import { AppContext, SttDialog } from '../../../sporttia/all';
import {
    formatPriceByLocale,
    isPaymentFormWithGateway,
} from '../../../lib/utils';
import useScsService from '../../../services/ScsService';
import constants from '../../../config/constants';

export default function BookingOperate({
    open = false,
    staticMsg,
    mship,
    fares,
    rateType,
    onClose,
    onBook,
    sc,
    finalPrice,
}: any) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const scsService = useScsService();

    const [boletos, setBoletos] = useState<any>();
    const [pfsToShow, setPfsToShow] = useState<string[]>([]);
    const [SCPrivileges, setSCPrivileges] = useState<number[]>([]);
    const [openTermsAndConditions, setOpenTermsAndConditions] = useState(false);
    const [paymentMethodSelected, setPaymentMethodSelected] = useState<
        string | null
    >(null);

    const CASHDRO = cxt?.sc?.cashdroConnected;

    const sportcenterPrivilegesQuery = scsService.useGetSportcenterPrivileges(
        sc?.id,
    );
    const scTpvConfsQuery = scsService.useGetScTpvConfs(sc?.id, {});

    // Handle TPV configurations
    useEffect(() => {
        if (scTpvConfsQuery.data) {
            const hasZitycard = scTpvConfsQuery.data.rows.some(
                (tpv: any) =>
                    tpv.bank === constants.tpvConf.banks.zitycard.name,
            );

            if (hasZitycard) {
                setPfsToShow([
                    ...pfsToShow,
                    constants.payment.paymentForms.zitycard.name,
                ]);
            }
        }
    }, [scTpvConfsQuery.data]);

    const selectTpvMethod = (pf: any) => {
        if (sc?.termsAndConditions) {
            setOpenTermsAndConditions(true);
            setPaymentMethodSelected(pf.id);
        } else {
            onBook({ paymentForm: pf.id });
        }
    };

    /**
     * Get sport centers privileges data
     */
    useEffect(() => {
        if (
            cxt.user?.role === cxt.constants.roleUser ||
            cxt.user?.role === cxt.constants.roleTeacher
        ) {
            if (sc) {
                cxt.api('GET', `/scs/${sc.id}/me`, {
                    success: (r: any) => {
                        if (r.mship && r.mship.privileges) {
                            setSCPrivileges(r.mship.privileges);
                        }
                    },
                });
            }
        } else {
            const privileges = sportcenterPrivilegesQuery?.data as any;
            setSCPrivileges(
                privileges?.rows?.map(
                    (privilege: any) => privilege?.active && privilege?.id,
                ) || [],
            );
        }
    }, [sc, sportcenterPrivilegesQuery?.data]);

    /**
     * DATOS:
     * - Privilegio 13: Permite pago online.
     * - Privilegio 6: PENDIENTE
     * - Privilegio 26: MONEDERO
     * - Privilegio 27: TPV
     */

    /**
     * Set payments to show
     */
    useEffect(() => {
        if (
            cxt.user?.role === cxt.constants.roleUser ||
            cxt.user?.role === cxt.constants.roleTeacher
        ) {
            const options = [];

            if ((Array.isArray(SCPrivileges) ? SCPrivileges : []).includes(6)) {
                options.push('');
            }
            if (
                (Array.isArray(SCPrivileges) ? SCPrivileges : []).includes(26)
            ) {
                options.push('PURSE');
            }
            if (
                (Array.isArray(SCPrivileges) ? SCPrivileges : []).includes(27)
            ) {
                options.push('TPV');
            }
            setPfsToShow([...pfsToShow, ...options]);
        } else if (cxt.user?.role === cxt.constants.roleSC) {
            const options = ['', 'CASH', 'BANK', 'FREE'];
            if (cxt.sc?.dataphoneConnected) {
                options.push('DATAFONO_CONNECTED');
            } else {
                options.push('DATAFONO');
            }
            if (
                (Array.isArray(SCPrivileges) ? SCPrivileges : []).includes(26)
            ) {
                options.push('PURSE');
            }

            if (CASHDRO) {
                options.push('CASHDRO');
            }
            setPfsToShow([...pfsToShow, ...options]);
        }
    }, [
        cxt.user?.role,
        SCPrivileges,
        mship,
        sportcenterPrivilegesQuery.data,
        finalPrice,
    ]);

    /**
     * fares or rateType change -> set boletos
     */
    useEffect(() => {
        if (fares && rateType) {
            if (rateType === cxt.constants.RT_Complete) {
                setBoletos(fares.fullBonos);
            } else {
                setBoletos(fares.individualBonos);
            }
        }
    }, [fares, rateType]);

    // Render
    return (
        <>
            <SttDialog
                title={t('timetable.book')}
                open={open}
                onClose={onClose}
                content={
                    <>
                        {staticMsg && (
                            <Box mb={3}>
                                <Alert variant="filled" severity="warning">
                                    {staticMsg}
                                </Alert>
                            </Box>
                        )}

                        <>
                            {cxt.constants.paymentForms
                                .filter((pf: any) => pfsToShow.includes(pf.id))
                                .map((pf: any) => (
                                    <Box key={pf?.id} my={1}>
                                        <Button
                                            fullWidth
                                            color="primary"
                                            variant="contained"
                                            onClick={() => {
                                                if (
                                                    isPaymentFormWithGateway(
                                                        pf.id,
                                                    )
                                                ) {
                                                    selectTpvMethod(pf);
                                                } else {
                                                    onBook({
                                                        paymentForm: pf.id,
                                                    });
                                                }
                                            }}
                                        >
                                            {pf.label}
                                            {mship && pf.id === 'PURSE' && (
                                                <span>
                                                    &nbsp;&nbsp;[{' '}
                                                    {formatPriceByLocale(
                                                        mship?.amount,
                                                        mship?.amountShape
                                                            ?.currency,
                                                        mship?.amountShape
                                                            ?.locale,
                                                    )}{' '}
                                                    ]{' '}
                                                </span>
                                            )}
                                        </Button>
                                    </Box>
                                ))}

                            {boletos && (
                                <Box mt={4}>
                                    {boletos.map((boleto: any) => (
                                        <Box key={boleto?.id} my={1}>
                                            <Button
                                                fullWidth
                                                color="primary"
                                                variant="contained"
                                                onClick={() =>
                                                    onBook({
                                                        idBoleto: boleto.id,
                                                    })
                                                }
                                            >
                                                {boleto.bono.name} [{' '}
                                                {boleto.amount -
                                                    boleto.consumed}{' '}
                                                ]
                                            </Button>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </>
                    </>
                }
                buttons={[
                    {
                        type: 'close',
                        onClick: onClose,
                    },
                ]}
            />

            <SttDialog
                title={t('booking.termsAndConditions')}
                maxWidth="xl"
                open={openTermsAndConditions}
                onClose={() => setOpenTermsAndConditions(false)}
                content={
                    <div
                        dangerouslySetInnerHTML={{
                            __html: sc?.termsAndConditions,
                        }}
                    />
                }
                buttons={[
                    {
                        type: 'accept',
                        onClick: () =>
                            onBook({ paymentForm: paymentMethodSelected }),
                    },
                    {
                        type: 'close',
                        onClick: () => setOpenTermsAndConditions(false),
                    },
                ]}
            />
        </>
    );
}
